import { useEffect, useCallback } from 'react';
import { dispatcher } from 'dispatcher';
import { loggerService } from 'js/services/logger.service';

import * as hooks from './hooks';

export const usePublicApi = () => {
    const fb = hooks.useFbPublicAPI();
    const menu = hooks.useMenuPublicAPI();
    const user = hooks.useUserPublicAPI();
    const jumio = hooks.useJumioPublicAPI();
    const device = hooks.useDevicePublicAPI();
    const language = hooks.useLanguagePublicAPI();
    const popup = hooks.usePopupPublicAPI();
    const auth = hooks.useAuthPublicAPI();
    const events = hooks.useEventsAPI();
    const server = hooks.useServerPublicAPI();
    const game = hooks.useGamePublicAPI();
    const htcmd = hooks.useHTCMDPublicAPI();
    const payments = hooks.usePaymentsPublicAPI();
    const affiliate = hooks.useAffiliatePublicAPI();
    const referrer = hooks.useReferrerPublicAPI();
    const common = hooks.useCommonPublicAPI();
    const page = hooks.usePagePublicAPI();
    const redux = hooks.useReduxPublicAPI();
    const translation = hooks.useTranslationPublicAPI();
    const formatter = hooks.useFormatterPublicAPI();
    const sidebar = hooks.useSidebarPublicAPI();
    const responsibleGaming = hooks.useResponsibleGamingPublicAPI();
    const documentUpload = hooks.useDocumentUploadPublicAPI();
    const wallet = hooks.useWalletPublicAPI();
    const bonus = hooks.useBonusPublicAPI();

    const gameWrapperNoop = useCallback((publicApiPart) => {
        console.warn(`"Playtech.API.${publicApiPart}" is not applicable in Game Wrapper`);
    }, []);

    useEffect(() => {
        /**
         * Global object with all Playtech-related cool stuff.
         * @namespace
         * @global
         */
        window.Playtech = window.Playtech || {};
        const isGameWrapper = window.Playtech.Variables?.isGameWrapper || false;
        /**
         * Public API is available in run time of Portal SPA running.
         * @memberOf Playtech
         * @namespace Playtech.API
         */
        window.Playtech.API = {
            ...window.Playtech.API,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.fb
             */
            fb,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.menu
             */
            menu,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.user
             */
            user,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.device
             */
            device,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.events
             */
            events,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.popup
             */
            popup,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.translation
             */
            translation,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.auth
             */
            auth,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.server
             */
            server,
            /**
             * @deprecated Please avoid using this API and consult with Playtech team.
             * @memberOf Playtech.API
             * @namespace Playtech.API.jumio
             */
            jumio,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.game
             */
            game,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.htcmd
             */
            htcmd,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.payments
             */
            payments,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.affiliate
             */
            affiliate,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.referrer
             */
            referrer,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.page
             */
            page,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.language
             */
            language: {
                ...language,
                ...isGameWrapper && { setCurrentLanguage: () => gameWrapperNoop('language.setCurrentLanguage') }
            },
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.formatter
             */
            formatter,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.sidebar
             */
             sidebar,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.common
             */
            common,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.responsibleGaming
             */
            responsibleGaming,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.documentUpload
             */
            documentUpload,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.wallet
             */
            wallet,
            /**
             * @memberOf Playtech.API
             * @namespace Playtech.API.bonus
             */
            bonus,
            /**
             * Hidden section only available in debug mode
             */
            ...loggerService.isEnabled() ? {
                /**
                 * Returns redux
                 * @memberOf Playtech.API
                 * @returns {Object} {currentState: Object, dispatch: Function, enableLogger: Function, prevState: Object}
                 */
                redux: { ...window.Playtech.API?.redux, ...redux },
                /**
                 * Returns dispatcher
                 * @memberOf Playtech.API
                 * @returns {Object}
                 */
                dispatcher
            } : {}
        };
    }, [
        affiliate,
        auth,
        bonus,
        common,
        device,
        documentUpload,
        events,
        fb,
        formatter,
        game,
        gameWrapperNoop,
        htcmd,
        jumio,
        language,
        menu,
        page,
        payments,
        popup,
        redux,
        referrer,
        responsibleGaming,
        server,
        sidebar,
        translation,
        user,
        wallet
    ]);
};
